import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, CheckCircle, XCircle, RotateCcw, AlertCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import WaveformVisualizer from './WaveformVisualizer';
import { apiService } from '../../services/apiService';
import { deviceManager } from '../../services/deviceManager'; // Import DeviceManager

// Styled components
const RecorderContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const InstructionCard = styled.div`
  background: ${({ theme }) => theme.colors.dark.surface};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  text-align: center;
`;

const PhaseIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PhaseNumber = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.primary[500]}, 
    ${({ theme }) => theme.colors.accent.purple}
  );
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
`;

const PhaseText = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
`;

const PhraseDisplay = styled.div`
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 2px dashed ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin: ${({ theme }) => theme.spacing.md} 0;
  position: relative;
  overflow: hidden;
`;

const PhraseText = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  line-height: 1.4;
  margin: 0;
`;

const RecordingHint = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const RecordingArea = styled.div`
  margin: ${({ theme }) => theme.spacing.lg} 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  flex: 1;
`;

const VisualizerContainer = styled(motion.div)`
  width: 100%;
  height: 200px;
  background: ${({ theme }) => theme.colors.dark.surface};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const RecordButton = styled(motion.button)`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: none;
  background: ${({ isRecording, theme }) => 
    isRecording 
      ? `linear-gradient(135deg, ${theme.colors.accent.red}, ${theme.colors.accent.orange})`
      : `linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent.purple})`
  };
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  box-shadow: 0 4px 20px ${({ theme }) => theme.colors.primary[500]}40;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 6px 30px ${({ theme }) => theme.colors.primary[500]}60;
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 32px;
    height: 32px;
    color: white;
  }
`;

const RetryButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  margin: ${({ theme }) => theme.spacing.md} auto 0;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary[500]};
    color: ${({ theme }) => theme.colors.primary[400]};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ProgressBar = styled.div`
  height: 4px;
  background: ${({ theme }) => theme.colors.dark.elevated};
  border-radius: 2px;
  margin-top: ${({ theme }) => theme.spacing.md};
  overflow: hidden;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, 
    ${({ theme }) => theme.colors.primary[500]}, 
    ${({ theme }) => theme.colors.accent.purple}
  );
  border-radius: 2px;
`;

const StatusMessage = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ type, theme }) => {
    switch(type) {
      case 'success': return `${theme.colors.status.online}20`;
      case 'error': return `${theme.colors.accent.red}20`;
      default: return theme.colors.dark.elevated;
    }
  }};
  border: 1px solid ${({ type, theme }) => {
    switch(type) {
      case 'success': return theme.colors.status.online;
      case 'error': return theme.colors.accent.red;
      default: return theme.colors.dark.border;
    }
  }};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin: ${({ theme }) => theme.spacing.md} 0;
  
  svg {
    width: 20px;
    height: 20px;
    color: ${({ type, theme }) => {
      switch(type) {
        case 'success': return theme.colors.status.online;
        case 'error': return theme.colors.accent.red;
        default: return theme.colors.dark.text;
      }
    }};
  }
  
  span {
    font-size: 0.875rem;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.dark.text};
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  font-size: 0.875rem;
  margin: ${({ theme }) => theme.spacing.lg} 0;
`;

// Main Component
const VoiceRecorder = ({ onComplete }) => {
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [currentPhrase, setCurrentPhrase] = useState(0);
  const [completedPhrases, setCompletedPhrases] = useState([]);
  const [audioStream, setAudioStream] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [phrases, setPhrases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const allSamplesRef = useRef([]);

  // Initialize enrollment session on mount
  useEffect(() => {
    startEnrollmentSession();
    requestMicrophoneAccess();
    
    // Listen for device changes
    const handleDeviceChange = () => {
      console.log('🔄 [VoiceRecorder] Device change detected');
      // Reinitialize audio stream if needed
      initializeAudioStream();
    };
    
    deviceManager.on('deviceChange', handleDeviceChange);
    
    return () => {
      deviceManager.removeListener('deviceChange', handleDeviceChange);
      cleanupAudioStream();
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startEnrollmentSession = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('sarvik_token');
      
      if (!token) {
        setStatusMessage({
          type: 'error',
          text: 'Authentication required. Please login again.'
        });
        setIsLoading(false);
        return;
      }

      const result = await apiService.startVoiceEnrollment(token);
      
      if (result.session_id) {
        setSessionId(result.session_id);
        
        // Transform phrases to match component structure
        const phrasesData = result.phrases.map((text, index) => ({
          text,
          hint: getHintForPhrase(index)
        }));
        setPhrases(phrasesData);
        setIsLoading(false);
      } else {
        throw new Error('Failed to start enrollment session');
      }
    } catch (error) {
      console.error('Failed to start enrollment:', error);
      setStatusMessage({
        type: 'error',
        text: 'Failed to start voice enrollment. Please refresh and try again.'
      });
      setIsLoading(false);
    }
  };

  const getHintForPhrase = (index) => {
    const hints = [
      "Speak clearly at your normal pace",
      "Use your everyday speaking voice",
      "Try to sound natural and relaxed"
    ];
    return hints[index] || "Speak naturally";
  };

  const initializeAudioStream = async () => {
    try {
      // Use DeviceManager to get audio stream
      const stream = deviceManager.getAudioStream();
      if (stream) {
        setAudioStream(stream);
      } else {
        // If no stream available, request access through DeviceManager
        await deviceManager.initialize();
        const newStream = deviceManager.getAudioStream();
        setAudioStream(newStream);
      }
    } catch (error) {
      console.error('Failed to initialize audio stream:', error);
      setStatusMessage({
        type: 'error',
        text: 'Microphone access is required. Please allow microphone access and refresh.'
      });
    }
  };

  const cleanupAudioStream = () => {
    if (audioStream) {
      // Don't stop the stream as DeviceManager manages it
      setAudioStream(null);
    }
  };

  const requestMicrophoneAccess = async () => {
    try {
      // Use DeviceManager to ensure microphone access
      await deviceManager.initialize();
      const stream = deviceManager.getAudioStream();
      setAudioStream(stream);
    } catch (error) {
      console.error('Microphone access denied:', error);
      setStatusMessage({
        type: 'error',
        text: 'Microphone access is required. Please allow microphone access and refresh.'
      });
    }
  };

  const startRecording = async () => {
    if (!audioStream) {
      await requestMicrophoneAccess();
      return;
    }

    if (!sessionId) {
      setStatusMessage({
        type: 'error',
        text: 'No enrollment session. Please refresh the page.'
      });
      return;
    }

    setIsRecording(true);
    setStatusMessage(null);
    audioChunksRef.current = [];

    const mediaRecorder = new MediaRecorder(audioStream, {
      mimeType: 'audio/webm;codecs=opus'
    });

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      verifyAndProcessSample(audioBlob);
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();

    // Auto-stop after 4 seconds
    setTimeout(() => {
      if (mediaRecorderRef.current?.state === 'recording') {
        stopRecording();
      }
    }, 4000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const verifyAndProcessSample = async (audioBlob) => {
    setIsProcessing(true);
    
    try {
      // Send to backend for processing
      const result = await apiService.recordEnrollmentPhrase(
        sessionId,
        currentPhrase,
        audioBlob
      );
      
      if (result.success) {
        handleSampleSuccess(audioBlob);
      } else {
        // Handle different types of errors
        if (result.retry) {
          setStatusMessage({
            type: 'error',
            text: result.reason || 'Please try again.'
          });
        } else {
          setStatusMessage({
            type: 'error',
            text: 'Failed to process recording. Please try again.'
          });
        }
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Failed to process sample:', error);
      setStatusMessage({
        type: 'error',
        text: 'Connection error. Please check your internet and try again.'
      });
      setIsProcessing(false);
    }
  };

  const handleSampleSuccess = async (audioBlob) => {
    const newSample = {
      phraseIndex: currentPhrase,
      phrase: phrases[currentPhrase]?.text,
      blob: audioBlob,
      timestamp: Date.now()
    };
    
    allSamplesRef.current.push(newSample);
    
    setStatusMessage({
      type: 'success',
      text: 'Great! Phrase recorded successfully.'
    });

    const updatedCompleted = [...completedPhrases, currentPhrase];
    setCompletedPhrases(updatedCompleted);

    setTimeout(async () => {
      setStatusMessage(null);
      
      if (currentPhrase + 1 >= phrases.length) {
        // All phrases complete - finalize enrollment
        try {
          const result = await apiService.completeEnrollment(sessionId);
          if (result.success) {
            console.log('Enrollment completed successfully');
            // Store quality score for display
            localStorage.setItem('voice_quality_score', result.quality_score);
            onComplete(allSamplesRef.current);
          } else {
            setStatusMessage({
              type: 'error',
              text: 'Failed to complete enrollment. Please try again.'
            });
          }
        } catch (error) {
          console.error('Failed to complete enrollment:', error);
          setStatusMessage({
            type: 'error',
            text: 'Failed to complete enrollment. Please try again.'
          });
        }
      } else {
        setCurrentPhrase(prev => prev + 1);
      }
      
      setIsProcessing(false);
    }, 2000);
  };

  const retryCurrentPhrase = () => {
    setStatusMessage(null);
  };

  // Calculate progress
  const progress = phrases.length > 0 ? ((completedPhrases.length / phrases.length) * 100) : 0;

  // Show loading state - optimized for background model loading
  if (isLoading) {
    // Don't show loading screen - models are loading in background
    // Just show the first phrase immediately
    return (
      <RecorderContainer>
        <InstructionCard>
          <PhaseIndicator>
            <PhaseNumber>1</PhaseNumber>
            <PhaseText>of 3 phrases</PhaseText>
          </PhaseIndicator>
          <PhraseDisplay>
            <PhraseText>SARVIK, what's the weather today?</PhraseText>
            <RecordingHint>Speak clearly at your normal pace</RecordingHint>
          </PhraseDisplay>
        </InstructionCard>
        <RecordingArea>
          <RecordButton
            isRecording={false}
            disabled={true}
            style={{ opacity: 0.7 }}
          >
            <Mic />
          </RecordButton>
        </RecordingArea>
      </RecorderContainer>
    );
  }

  // Show error if no phrases loaded
  if (!phrases.length) {
    return (
      <RecorderContainer>
        <StatusMessage type="error">
          <AlertCircle />
          <span>Failed to load enrollment data. Please refresh the page.</span>
        </StatusMessage>
      </RecorderContainer>
    );
  }

  const currentPhraseData = phrases[currentPhrase];

  if (!currentPhraseData) {
    return null;
  }

  return (
    <RecorderContainer>
      <InstructionCard>
        <PhaseIndicator>
          <PhaseNumber>{currentPhrase + 1}</PhaseNumber>
          <PhaseText>of {phrases.length} phrases</PhaseText>
        </PhaseIndicator>

        <PhraseDisplay>
          <PhraseText>{currentPhraseData.text}</PhraseText>
          <RecordingHint>{currentPhraseData.hint}</RecordingHint>
        </PhraseDisplay>

        <AnimatePresence>
          {statusMessage && (
            <StatusMessage
              type={statusMessage.type}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {statusMessage.type === 'success' ? <CheckCircle /> : <AlertCircle />}
              <span>{statusMessage.text}</span>
            </StatusMessage>
          )}
        </AnimatePresence>
      </InstructionCard>

      <RecordingArea>
        <AnimatePresence mode="wait">
          {(isRecording || isProcessing) ? (
            <VisualizerContainer
              key="visualizer"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <WaveformVisualizer 
                audioStream={audioStream} 
                isProcessing={isProcessing}
                processingText={isProcessing ? "Analyzing your voice..." : null}
              />
            </VisualizerContainer>
          ) : (
            <motion.div
              key="button"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <RecordButton
                isRecording={false}
                onClick={startRecording}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={isProcessing || !audioStream || !sessionId}
              >
                <Mic />
              </RecordButton>
            </motion.div>
          )}
        </AnimatePresence>
      </RecordingArea>

      {!isRecording && !isProcessing && statusMessage?.type === 'error' && (
        <RetryButton onClick={retryCurrentPhrase}>
          <RefreshCw />
          Retry
        </RetryButton>
      )}

      <ProgressBar>
        <ProgressFill 
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
        />
      </ProgressBar>
    </RecorderContainer>
  );
};

export default VoiceRecorder;